import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface EasyShip {
  _id: string;
  accessToken?: string;
  originAddressId?: string;
}

type EasyShipSyncNdjsonEvent =
  | { type: "init"; total: number; productCount?: number }
  | {
      type: "payment";
      index: number;
      total: number;
      result: {
        paymentId: string;
        ok: boolean;
        easyShipmentIds?: string[];
        error?: string;
        statusCode?: number;
      };
    }
  | { type: "complete"; total: number; productCount?: number; results: Array<{ ok?: boolean }> }
  | { type: "error"; message: string };

type SyncProgressState = {
  kind: "sync-new" | "resync-all";
  total?: number;
  productCount?: number;
  index: number;
  succeeded: number;
  failed: number;
};

async function consumeEasyShipSyncNdjsonStream({
  response,
  onEvent,
}: {
  response: Response;
  onEvent: (event: EasyShipSyncNdjsonEvent) => void;
}): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("No response body");
  }
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      onEvent(JSON.parse(trimmed) as EasyShipSyncNdjsonEvent);
    }
  }
  const tail = buffer.trim();
  if (tail) {
    onEvent(JSON.parse(tail) as EasyShipSyncNdjsonEvent);
  }
}

export function SettingsEasyShip() {
  const [easyShip, setEasyShip] = useState<EasyShip | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingEasyShip, setIsCreatingEasyShip] = useState(false);
  const [isDeletingEasyShip, setIsDeletingEasyShip] = useState(false);
  const [isSyncingNew, setIsSyncingNew] = useState(false);
  const [isResyncingAll, setIsResyncingAll] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgressState | null>(null);
  const [isSavingEasyShip, setIsSavingEasyShip] = useState(false);
  const [showEasyShipAccessToken, setShowEasyShipAccessToken] = useState(false);
  const [easyShipAccessToken, setEasyShipAccessToken] = useState("");
  const [easyShipOriginAddressId, setEasyShipOriginAddressId] = useState("");

  useEffect(() => {
    async function fetchEasyShip() {
      setIsLoading(true);
      try {
        const easyShipResponse = await fetch("/api/easyship");
        if (easyShipResponse.ok) {
          const easyShipData = await easyShipResponse.json();
          if (easyShipData.easyShip) {
            setEasyShip(easyShipData.easyShip);
            setEasyShipAccessToken(easyShipData.easyShip.accessToken || "");
            setEasyShipOriginAddressId(easyShipData.easyShip.originAddressId || "");
          }
        } else if (easyShipResponse.status !== 404) {
          console.error("Failed to fetch EasyShip integration");
        }
      } catch (error) {
        console.error("Failed to fetch EasyShip integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchEasyShip();
  }, []);

  const handleCreateEasyShip = async () => {
    setIsCreatingEasyShip(true);
    try {
      const response = await fetch("/api/easyship", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.easyShip) {
          setEasyShip(data.easyShip);
          setEasyShipAccessToken(data.easyShip.accessToken || "");
          setEasyShipOriginAddressId(data.easyShip.originAddressId || "");
          toast.success("EasyShip integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create EasyShip integration");
      }
    } catch (error) {
      console.error("Error creating EasyShip integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create EasyShip integration");
    } finally {
      setIsCreatingEasyShip(false);
    }
  };

  const handleSaveEasyShip = async () => {
    if (!easyShip) return;

    setIsSavingEasyShip(true);
    try {
      const response = await fetch("/api/easyship", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          accessToken: easyShipAccessToken,
          originAddressId: easyShipOriginAddressId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.easyShip) {
          setEasyShip(data.easyShip);
          setEasyShipOriginAddressId(data.easyShip.originAddressId || "");
          toast.success("EasyShip settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save EasyShip settings");
      }
    } catch (error) {
      console.error("Error saving EasyShip settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save EasyShip settings");
    } finally {
      setIsSavingEasyShip(false);
    }
  };

  const handleResyncAll = async () => {
    if (!easyShip) return;
    setIsResyncingAll(true);
    setSyncProgress({ kind: "resync-all", index: 0, succeeded: 0, failed: 0 });
    try {
      const response = await fetch("/api/easyship/resync-all", {
        method: "POST",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.error === "string" ? data.error : "Re-sync All failed");
      }
      await consumeEasyShipSyncNdjsonStream({
        response,
        onEvent: (ev) => {
          if (ev.type === "init") {
            setSyncProgress((p) => (p ? { ...p, total: ev.total } : p));
          } else if (ev.type === "payment") {
            setSyncProgress((p) =>
              p ?
                {
                  ...p,
                  index: ev.index,
                  total: ev.total,
                  succeeded: p.succeeded + (ev.result.ok ? 1 : 0),
                  failed: p.failed + (ev.result.ok ? 0 : 1),
                }
              : p,
            );
          } else if (ev.type === "complete") {
            const total = ev.total;
            const succeeded = ev.results.filter((row) => row.ok === true).length;
            toast.success(
              total === 0 ?
                "Re-sync All: no payments with EasyShip shipments"
              : `Re-sync All: ${succeeded}/${total} payment(s) re-synced`,
            );
          } else if (ev.type === "error") {
            throw new Error(ev.message);
          }
        },
      });
    } catch (error) {
      console.error("Re-sync All error:", error);
      toast.error(error instanceof Error ? error.message : "Re-sync All failed");
    } finally {
      setIsResyncingAll(false);
      setSyncProgress(null);
    }
  };

  const handleSyncNew = async () => {
    if (!easyShip) return;
    setIsSyncingNew(true);
    setSyncProgress({ kind: "sync-new", index: 0, succeeded: 0, failed: 0 });
    try {
      const response = await fetch("/api/easyship/sync-new", {
        method: "POST",
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.error === "string" ? data.error : "Sync New failed");
      }
      await consumeEasyShipSyncNdjsonStream({
        response,
        onEvent: (ev) => {
          if (ev.type === "init") {
            setSyncProgress((p) =>
              p ? { ...p, total: ev.total, productCount: ev.productCount } : p,
            );
          } else if (ev.type === "payment") {
            setSyncProgress((p) =>
              p ?
                {
                  ...p,
                  index: ev.index,
                  total: ev.total,
                  succeeded: p.succeeded + (ev.result.ok ? 1 : 0),
                  failed: p.failed + (ev.result.ok ? 0 : 1),
                }
              : p,
            );
          } else if (ev.type === "complete") {
            const total = ev.total;
            const succeeded = ev.results.filter((row) => row.ok === true).length;
            toast.success(
              total === 0 ?
                "Sync New: no payments to sync"
              : `Sync New: ${succeeded}/${total} payment(s) synced to EasyShip`,
            );
          } else if (ev.type === "error") {
            throw new Error(ev.message);
          }
        },
      });
    } catch (error) {
      console.error("Sync New error:", error);
      toast.error(error instanceof Error ? error.message : "Sync New failed");
    } finally {
      setIsSyncingNew(false);
      setSyncProgress(null);
    }
  };

  const handleDeleteEasyShip = async () => {
    if (!easyShip) return;

    if (!confirm("Are you sure you want to delete your EasyShip integration?")) {
      return;
    }

    setIsDeletingEasyShip(true);
    try {
      const response = await fetch(`/api/easyship/${easyShip._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setEasyShip(null);
        setEasyShipAccessToken("");
        setEasyShipOriginAddressId("");
        toast.success("EasyShip integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete EasyShip integration");
      }
    } catch (error) {
      console.error("Error deleting EasyShip integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete EasyShip integration");
    } finally {
      setIsDeletingEasyShip(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 flex-1 min-w-0">
            <h3 className="text-lg font-semibold">EasyShip</h3>
            <p className="text-sm text-muted-foreground">
              Connect Easyship to manage shipping labels and rates from your dashboard
            </p>
            {easyShip && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">
                  Connected
                </p>
              </div>
            )}
          </div>
          <div className="shrink-0">
            {!easyShip ? (
              <Button onClick={handleCreateEasyShip} disabled={isCreatingEasyShip}>
                {isCreatingEasyShip ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    aria-label="EasyShip actions"
                    disabled={isDeletingEasyShip || isSyncingNew || isResyncingAll}
                  >
                    {isDeletingEasyShip || isSyncingNew || isResyncingAll ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MoreHorizontal className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={isSyncingNew || isDeletingEasyShip || isResyncingAll}
                    onClick={() => {
                      handleSyncNew();
                    }}
                  >
                    {isSyncingNew ? "Sync New…" : "Sync New"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isResyncingAll || isDeletingEasyShip || isSyncingNew}
                    onClick={() => {
                      handleResyncAll();
                    }}
                  >
                    {isResyncingAll ? "Re-sync All…" : "Re-sync All"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={isDeletingEasyShip || isSyncingNew || isResyncingAll}
                    onClick={() => {
                      handleDeleteEasyShip();
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {easyShip && syncProgress && (isSyncingNew || isResyncingAll) && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm space-y-2">
            <div className="font-medium text-foreground">
              {syncProgress.kind === "sync-new" ? "Sync New" : "Re-sync All"}
            </div>
            <div className="text-muted-foreground">
              {syncProgress.total === undefined ?
                "Starting…"
              : syncProgress.total === 0 ?
                "No payments to process."
              : `Payment ${syncProgress.index} of ${syncProgress.total} · ${syncProgress.succeeded} succeeded, ${syncProgress.failed} failed`}
            </div>
            {syncProgress.total !== undefined && syncProgress.total > 0 && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary transition-[width] duration-300 ease-out"
                  style={{
                    width: `${Math.min(100, (syncProgress.index / syncProgress.total) * 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
        )}
        {easyShip && (
          <div className="space-y-2 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="easyShipAccessToken">Access token</Label>
              <div className="relative">
                <Input
                  id="easyShipAccessToken"
                  type={showEasyShipAccessToken ? "text" : "password"}
                  value={easyShipAccessToken}
                  onChange={(e) => setEasyShipAccessToken(e.target.value)}
                  placeholder="Enter Easyship API access token"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowEasyShipAccessToken(!showEasyShipAccessToken)}
                >
                  {showEasyShipAccessToken ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Syncing orders to Easyship requires an origin address UUID from your Easyship account (saved under Addresses).
            </p>
            <div className="space-y-2">
              <Label htmlFor="easyShipOriginAddressId">Origin address ID</Label>
              <Input
                id="easyShipOriginAddressId"
                type="text"
                value={easyShipOriginAddressId}
                onChange={(e) => setEasyShipOriginAddressId(e.target.value)}
                placeholder="UUID saved in Easyship → Addresses"
                className="font-mono text-sm"
              />
            </div>
            <Button onClick={handleSaveEasyShip} disabled={isSavingEasyShip}>
              {isSavingEasyShip ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
